import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Badge, Nav, Tab, Button, Spinner, Alert, Modal, Form, InputGroup } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { FaSlack } from 'react-icons/fa';
import ShareModal from '../components/ShareModal';
import axios from '../utils/axios';
import { useAuth } from '../utils/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const Summary = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('summary');
  const [showShareModal, setShowShareModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [meetingData, setMeetingData] = useState(null);
  const [pollingInterval, setPollingInterval] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [slackPosting, setSlackPosting] = useState(false);
  const [slackSuccess, setSlackSuccess] = useState(false);

  // Chat related states
  const [chatInput, setChatInput] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const chatContainerRef = useRef(null);

  // Function to fetch meeting data from API
  const fetchMeetingData = async () => {
    try {
      const response = await axios.get(`/api/meetings/${id}`);
      
      if (response.data && response.data.meeting) {
        setMeetingData(response.data.meeting);
        
        // If meeting is completed, stop polling
        if (response.data.meeting.status === 'completed') {
          if (pollingInterval) {
            clearInterval(pollingInterval);
            setPollingInterval(null);
          }
        }
        
        setLoading(false);
      }
    } catch (error) {
      console.error('Error fetching meeting data:', error);
      setError('Error loading meeting data. Please try again later.');
      setLoading(false);
      
      if (pollingInterval) {
        clearInterval(pollingInterval);
        setPollingInterval(null);
      }
    }
  };

  useEffect(() => {
    // Initial fetch
    fetchMeetingData();
    
    // Poll for updates if meeting is processing
    const interval = setInterval(fetchMeetingData, 5000); // Check every 5 seconds
    setPollingInterval(interval);
    
    // Cleanup function
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Function to return to dashboard and update analytics
  const returnToDashboardWithAnalytics = async () => {
    try {
      // Update analytics data before navigating
      await axios.post('/api/analytics/update', {
        meetingId: id,
        action: 'meeting_completed'
      });
      
      // Navigate to dashboard
      navigate('/dashboard');
    } catch (error) {
      console.error('Error updating analytics:', error);
      // Still navigate to dashboard even if analytics update fails
      navigate('/dashboard');
    }
  };

  // Function to scroll chat to bottom when new messages arrive
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatHistory]);

  // Function to handle chat interaction with the meeting transcript and summary
  const handleChat = async () => {
    if (!chatInput.trim()) return;
    setChatLoading(true);
    setError('');
    
    try {
      // Store the user message in chat history immediately for better UX
      const userMessage = chatInput;
      setChatInput('');
      setChatHistory([...chatHistory, { sender: 'user', text: userMessage }]);
      
      if (!meetingData) {
        console.error('No meeting data available at all');
        setChatHistory([...chatHistory, 
          { sender: 'user', text: userMessage },
          { sender: 'ai', text: 'Sorry, I could not access any meeting data. Please try again later.' }
        ]);
        setChatLoading(false);
        return;
      }
      
      // Try to fetch transcript if not available, but don't fail if we can't get it
      // We'll just use tab data even without transcript
      if (!meetingData.transcript) {
        try {
          console.log('Trying to fetch transcript, but will continue without it if needed');
          const response = await axios.get(`/api/meetings/${id}?includeTranscript=true`);
          if (response.data && response.data.meeting && response.data.meeting.transcript) {
            console.log('Successfully fetched transcript as additional context');
            setMeetingData({...meetingData, transcript: response.data.meeting.transcript});
          } else {
            console.log('No transcript available, but continuing with tab data only');
          }
        } catch (err) {
          console.log('Could not fetch transcript, but continuing with tab data only:', err.message);
        }
      }
      
      // Gather all meeting data sections to provide comprehensive context for the AI
      const chatTranscript = meetingData.transcript || '';
      
      // Build a comprehensive context with all meeting information sections
      // Using an array and join method to avoid newline issues
      const contextParts = [];
      
      // Add summary section
      if (meetingData.summary) {
        contextParts.push('## Summary');
        contextParts.push(meetingData.summary);
        contextParts.push(''); // Empty line for spacing
      }
      
      // Add key discussion points
      if (meetingData.keyPoints && meetingData.keyPoints.length > 0) {
        contextParts.push('## Key Discussion Points');
        meetingData.keyPoints.forEach((point, index) => {
          contextParts.push(`${index + 1}. ${point}`);
        });
        contextParts.push(''); // Empty line for spacing
      }
      
      // Add action items
      if (meetingData.actionItems && meetingData.actionItems.length > 0) {
        contextParts.push('## Action Items');
        meetingData.actionItems.forEach((item, index) => {
          let itemText = `${index + 1}. ${item.task || item}`;
          if (item.assignee) itemText += ` (Assigned to: ${item.assignee})`;
          if (item.deadline) itemText += ` (Due: ${item.deadline})`;
          contextParts.push(itemText);
        });
        contextParts.push(''); // Empty line for spacing
      }
      
      // Add decisions made
      if (meetingData.decisions && meetingData.decisions.length > 0) {
        contextParts.push('## Decisions Made');
        meetingData.decisions.forEach((decision, index) => {
          contextParts.push(`${index + 1}. ${decision}`);
        });
        contextParts.push(''); // Empty line for spacing
      }
      
      // Add follow-up items
      if (meetingData.followUp && meetingData.followUp.length > 0) {
        contextParts.push('## Follow-up Items');
        meetingData.followUp.forEach((item, index) => {
          contextParts.push(`${index + 1}. ${item}`);
        });
        contextParts.push(''); // Empty line for spacing
      }
      
      // Add strategic recommendations
      if (meetingData.recommendations && meetingData.recommendations.length > 0) {
        contextParts.push('## Strategic Recommendations');
        meetingData.recommendations.forEach((rec, index) => {
          contextParts.push(`${index + 1}. ${rec}`);
        });
        contextParts.push(''); // Empty line for spacing
      }
      
      // Add risks and challenges
      if (meetingData.risks && meetingData.risks.length > 0) {
        contextParts.push('## Identified Risks & Challenges');
        meetingData.risks.forEach((risk, index) => {
          contextParts.push(`${index + 1}. ${risk}`);
        });
        contextParts.push(''); // Empty line for spacing
      }
      
      // Add business opportunities
      if (meetingData.opportunities && meetingData.opportunities.length > 0) {
        contextParts.push('## Business Opportunities');
        meetingData.opportunities.forEach((opp, index) => {
          contextParts.push(`${index + 1}. ${opp}`);
        });
        contextParts.push(''); // Empty line for spacing
      }
      
      // Join all parts with newlines - using a safer approach to avoid syntax issues
      const fullContext = contextParts.join(String.fromCharCode(10));

      // Get auth token for the API call
      let token = null;
      try {
        const storedToken = localStorage.getItem('token') ||
                           localStorage.getItem('authToken');
        if (storedToken) {
          token = storedToken;
        } else {
          const user = JSON.parse(localStorage.getItem('user'));
          if (user && user.token) {
            token = user.token;
          }
        }
      } catch (e) {
        console.error('Error retrieving authentication token:', e);
      }
      
      // For development, use a demo token if none found
      if (!token && process.env.NODE_ENV !== 'production') {
        token = 'demo-token-for-testing';
      }
      
      // Call the chat API with all available meeting information and user question
      // If the transcript is not available, we'll substitute the tab data as the transcript
      // This ensures the API will work even if it checks for transcript presence
      const response = await axios.post('/api/desktop-chat', {
        transcript: chatTranscript || fullContext,  // Use tab data as transcript if no transcript available
        summary: fullContext,  // Always send all tab data as the context
        message: userMessage
      }, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : ''
        }
      });
      
      // Add AI response to chat history
      setChatHistory([...chatHistory, 
        { sender: 'user', text: userMessage },
        { sender: 'ai', text: response.data.reply }
      ]);
    } catch (err) {
      console.error('Chat error:', err);
      // Add error message to chat
      setChatHistory([...chatHistory, 
        { sender: 'user', text: chatInput },
        { sender: 'ai', text: 'Sorry, I encountered an error. Please try again.' }
      ]);
    } finally {
      setChatLoading(false);
    }
  };

  // Handle keypress in chat input (for Enter key)
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleChat();
    }
  };

  // Function to handle meeting deletion
  const handleDelete = async () => {
    if (!id) {
      setError('Cannot delete: Meeting ID not found');
      return;
    }
    
    setDeleting(true);
    try {
      // Call the delete endpoint
      await axios.delete(`/api/meetings/${id}`);
      
      // Close the modal
      setShowDeleteModal(false);
      
      // Show success message and navigate to dashboard
      navigate('/dashboard', { state: { message: 'Meeting deleted successfully', type: 'success' } });
    } catch (error) {
      console.error('Error deleting meeting:', error);
      setError('Failed to delete meeting. Please try again.');
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  // Function to share to Slack
  const handleShareToSlack = async () => {
    if (!id) {
      setError('Cannot share: Meeting ID not found');
      return;
    }
    
    const currentToken = token || localStorage.getItem('token');
    if (!currentToken) {
      setError('Please log in to share to Slack');
      return;
    }
    
    setSlackPosting(true);
    setSlackSuccess(false);
    
    try {
      const response = await fetch(`${API_URL}/api/integrations/slack/post-summary/${id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${currentToken}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSlackSuccess(true);
        setTimeout(() => setSlackSuccess(false), 3000);
      } else {
        if (data.message?.includes('not connected')) {
          setError('Slack not connected. Go to Profile > Integrations to connect.');
        } else if (data.message?.includes('channel')) {
          setError('No Slack channel configured. Go to Profile > Integrations to set up.');
        } else {
          setError(data.message || 'Failed to post to Slack');
        }
      }
    } catch (err) {
      console.error('Error posting to Slack:', err);
      setError('Failed to post to Slack. Please try again.');
    } finally {
      setSlackPosting(false);
    }
  };

  // Function to share chat response to Slack
  const handleShareChatToSlack = async (messageText, question) => {
    const currentToken = token || localStorage.getItem('token');
    if (!currentToken) {
      setError('Please log in to share to Slack');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/integrations/slack/post-message`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${currentToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: `AI Insight: ${meetingData?.title || 'Meeting'}`,
          question: question,
          content: messageText,
          meetingId: id
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSlackSuccess(true);
        setTimeout(() => setSlackSuccess(false), 3000);
      } else {
        setError(data.message || 'Failed to share to Slack');
      }
    } catch (err) {
      console.error('Error sharing to Slack:', err);
      setError('Failed to share to Slack');
    }
  };

  // Function to handle export
  const handleExport = async () => {
    if (!id) {
      setError('Cannot export: Meeting ID not found');
      return;
    }
    
    setExporting(true);
    try {
      // Request the server to generate an export
      const response = await axios.get(`/api/meetings/${id}/export`, {
        responseType: 'blob'  // Important for handling file downloads
      });
      
      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${meetingData.title || 'meeting'}_export.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      // Clean up
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting meeting:', error);
      setError('Failed to export meeting content. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  // Handle loading state
  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" variant="primary" />
        <h4 className="mt-3 text-white">Loading meeting data...</h4>
      </Container>
    );
  }

  // Handle error state
  if (error) {
    return (
      <Container className="py-5">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  // Handle processing state
  if (meetingData && meetingData.status === 'processing') {
    return (
      <Container className="py-5 text-center">
        <Card className="glass-card">
          <Card.Body className="p-5">
            <div className="mb-4">
              <Spinner animation="border" variant="primary" />
            </div>
            <h3 className="text-white mb-3">{meetingData.title || 'Your meeting'} is being processed</h3>
            <p className="text-white">
              We're analyzing your recording to generate a comprehensive summary, extract key points,
              identify action items, and provide recommendations. This process may take a few minutes.
            </p>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <Container fluid className="p-2 p-md-4">
      <Row className="mb-3 mb-md-4">
        <Col xs={12} md={8}>
          <h1 className="mb-2 h4 h1-md">{meetingData.title}</h1>
          <div className="d-flex flex-wrap align-items-center small">
            <Badge bg="dark" className="badge-success me-2 mb-1 p-1 p-md-2">
              Summarized
            </Badge>
            <span className="text-secondary me-2 mb-1">
              <i className="bi bi-calendar me-1"></i> {formatDate(meetingData.meetingDate || meetingData.createdAt)}
            </span>
            <span className="text-secondary me-2 mb-1 d-none d-sm-inline">
              <i className="bi bi-clock me-1"></i> {meetingData.duration}
            </span>
          </div>
        </Col>
        <Col xs={12} md={4} className="d-flex flex-wrap gap-1 justify-content-start justify-content-md-end mt-2 mt-md-0">
          <Button 
            variant="outline-primary" 
            size="sm"
            onClick={returnToDashboardWithAnalytics}
          >
            <i className="bi bi-arrow-left me-1"></i>Back
          </Button>
          <Button 
            variant="outline-primary" 
            size="sm"
            onClick={() => setShowShareModal(true)}
          >
            <i className="bi bi-share me-1"></i>Share
          </Button>
          <Button 
            variant="outline-primary"
            size="sm"
            onClick={handleShareToSlack}
            disabled={slackPosting}
            title="Share to Slack"
          >
            <FaSlack /><span className="d-none d-sm-inline ms-1">{slackPosting ? '...' : slackSuccess ? 'Sent!' : 'Slack'}</span>
          </Button>
          <Button 
            variant="outline-primary"
            size="sm"
            onClick={handleExport}
            disabled={exporting}
          >
            <i className="bi bi-download me-1"></i><span className="d-none d-sm-inline">{exporting ? 'Exporting...' : 'Export'}</span>
          </Button>
          <Button 
            variant="outline-danger"
            size="sm"
            onClick={() => setShowDeleteModal(true)}
            disabled={deleting}
          >
            <i className="bi bi-trash"></i><span className="d-none d-sm-inline ms-1">Delete</span>
          </Button>
        </Col>
      </Row>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Meeting</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete this meeting? This action cannot be undone.</p>
          <p><strong>Title:</strong> {meetingData?.title}</p>
          <p><strong>Date:</strong> {formatDate(meetingData?.createdAt)}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? 'Deleting...' : 'Delete Meeting'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Row>
        <Col md={3} className="mb-4">
          <Card className="glass-card mb-4">
            <Card.Body>
              <h5 className="mb-3">Participants</h5>
              <div className="d-flex flex-column">
                {meetingData.participants && meetingData.participants.map((participant, index) => (
                  <span key={index} className="mb-2 text-secondary small">
                    {participant}
                  </span>
                ))}
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={9}>
          <Card className="glass-card mb-4">
            <Card.Body>
              <Tab.Container id="summary-tabs" defaultActiveKey="summary">
                <Nav variant="tabs" className="mb-3 meeting-tabs flex-wrap">
                  <Nav.Item>
                    <Nav.Link 
                      eventKey="summary"
                      onClick={() => setActiveTab('summary')}
                      className={activeTab === 'summary' ? 'active' : ''}
                    >
                      Summary
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link 
                      eventKey="key-points"
                      onClick={() => setActiveTab('key-points')}
                      className={activeTab === 'key-points' ? 'active' : ''}
                    >
                      <span className="d-none d-md-inline">Key Discussion Points</span>
                      <span className="d-md-none">Key Points</span>
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link 
                      eventKey="action-items"
                      onClick={() => setActiveTab('action-items')}
                      className={activeTab === 'action-items' ? 'active' : ''}
                    >
                      <span className="d-none d-md-inline">Action Items</span>
                      <span className="d-md-none">Actions</span>
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link 
                      eventKey="decisions"
                      onClick={() => setActiveTab('decisions')}
                      className={activeTab === 'decisions' ? 'active' : ''}
                    >
                      <span className="d-none d-md-inline">Decisions Made</span>
                      <span className="d-md-none">Decisions</span>
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link 
                      eventKey="follow-up"
                      onClick={() => setActiveTab('follow-up')}
                      className={activeTab === 'follow-up' ? 'active' : ''}
                    >
                      <span className="d-none d-md-inline">Follow-up Items</span>
                      <span className="d-md-none">Follow-up</span>
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link 
                      eventKey="recommendations"
                      onClick={() => setActiveTab('recommendations')}
                      className={activeTab === 'recommendations' ? 'active' : ''}
                    >
                      <span className="d-none d-md-inline">Strategic Recommendations</span>
                      <span className="d-md-none">Tips</span>
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link 
                      eventKey="risks"
                      onClick={() => setActiveTab('risks')}
                      className={activeTab === 'risks' ? 'active' : ''}
                    >
                      <span className="d-none d-md-inline">Identified Risks & Challenges</span>
                      <span className="d-md-none">Risks</span>
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link 
                      eventKey="opportunities"
                      onClick={() => setActiveTab('opportunities')}
                      className={activeTab === 'opportunities' ? 'active' : ''}
                    >
                      <span className="d-none d-md-inline">Business Opportunities</span>
                      <span className="d-md-none">Opps</span>
                    </Nav.Link>
                  </Nav.Item>
                </Nav>
                <Tab.Content>
                  <Tab.Pane eventKey="summary">
                    <h4 className="mb-3">Meeting Summary</h4>
                    <p className="text-secondary">{meetingData.summary}</p>
                  </Tab.Pane>
                  <Tab.Pane eventKey="key-points">
                    <h4 className="mb-3">Key Discussion Points</h4>
                    <ul className="text-secondary">
                      {meetingData.keyPoints && meetingData.keyPoints.map((point, index) => (
                        <li key={index} className="mb-2">{point}</li>
                      ))}
                    </ul>
                  </Tab.Pane>
                  <Tab.Pane eventKey="action-items">
                    <h4 className="mb-3">Action Items</h4>
                    {meetingData.actionItems && meetingData.actionItems.length > 0 ? (
                      meetingData.actionItems.map((item, index) => (
                        <Card key={index} className="mb-3">
                          <Card.Body>
                            <div className="d-flex align-items-start justify-content-between">
                              <div>
                                <h6>{item.task}</h6>
                                {item.assignee && <p className="text-secondary mb-0">Assigned to: {item.assignee}</p>}
                              </div>
                              {item.deadline && (
                                <Badge bg="dark" className="badge-warning p-2">
                                  Due: {item.deadline}
                                </Badge>
                              )}
                            </div>
                          </Card.Body>
                        </Card>
                      ))
                    ) : (
                      <p className="text-secondary">No action items identified in this meeting.</p>
                    )}
                  </Tab.Pane>
                  <Tab.Pane eventKey="decisions">
                    <h4 className="mb-3">Decisions Made</h4>
                    {meetingData.decisions && meetingData.decisions.length > 0 ? (
                      <ul className="text-secondary">
                        {meetingData.decisions.map((decision, index) => (
                          <li key={index} className="mb-2">{decision}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-secondary">No decisions were identified in this meeting.</p>
                    )}
                  </Tab.Pane>
                  <Tab.Pane eventKey="follow-up">
                    <h4 className="mb-3">Follow-up Items</h4>
                    {meetingData.followUp && meetingData.followUp.length > 0 ? (
                      <ul className="text-secondary">
                        {meetingData.followUp.map((item, index) => (
                          <li key={index} className="mb-2">{item}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-secondary">No follow-up items were identified.</p>
                    )}
                  </Tab.Pane>
                  <Tab.Pane eventKey="recommendations">
                    <h4 className="mb-3">Strategic Recommendations</h4>
                    {meetingData.recommendations && meetingData.recommendations.length > 0 ? (
                      <ul className="text-secondary">
                        {meetingData.recommendations.map((rec, index) => (
                          <li key={index} className="mb-2">{rec}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-secondary">No recommendations were generated for this meeting.</p>
                    )}
                  </Tab.Pane>
                  <Tab.Pane eventKey="risks">
                    <h4 className="mb-3">Identified Risks & Challenges</h4>
                    {meetingData.risks && meetingData.risks.length > 0 ? (
                      <ul className="text-secondary">
                        {meetingData.risks.map((risk, index) => (
                          <li key={index} className="mb-2">{risk}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-secondary">No risks or challenges were identified in this meeting.</p>
                    )}
                  </Tab.Pane>
                  <Tab.Pane eventKey="opportunities">
                    <h4 className="mb-3">Business Opportunities</h4>
                    {meetingData.opportunities && meetingData.opportunities.length > 0 ? (
                      <ul className="text-secondary">
                        {meetingData.opportunities.map((opportunity, index) => (
                          <li key={index} className="mb-2">{opportunity}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-secondary">No specific business opportunities were identified.</p>
                    )}
                  </Tab.Pane>
                </Tab.Content>
              </Tab.Container>
            </Card.Body>
          </Card>
          
          {/* Chat Interface */}
          <Card className="glass-card mb-4">
            <Card.Body>
              {/* Quick action buttons */}
              <div className="mb-3 d-flex gap-2 flex-wrap">
                <Button variant="outline-primary" size="sm" disabled={chatLoading} onClick={() => { setChatInput('Summarize this transcript'); handleChat(); }}>
                  Summarize
                </Button>
                <Button variant="outline-success" size="sm" disabled={chatLoading} onClick={() => { setChatInput('Extract all to-do items from this transcript'); handleChat(); }}>
                  Extract To-Do
                </Button>
                <Button variant="outline-warning" size="sm" disabled={chatLoading} onClick={() => { setChatInput('Generate an email based on this transcript'); handleChat(); }}>
                  Generate Email
                </Button>
              </div>
              
              {/* Chat messages */}
              <div ref={chatContainerRef} className="border rounded p-3 mb-3" style={{ height: '300px', overflowY: 'auto', background: '#fff' }}>
                {chatHistory.length === 0 ? (
                  <div className="text-center text-muted my-5">
                    Ask anything about this transcript using the chat input below...
                  </div>
                ) : (
                  chatHistory.map((msg, idx) => (
                    <div key={idx} className={`d-flex ${msg.sender === 'user' ? 'justify-content-end' : 'justify-content-start'} mb-3`}>
                      <div
                        className={`p-3 rounded-3 ${msg.sender === 'user' ? 'bg-primary text-white' : 'bg-light'}`}
                        style={{ maxWidth: '80%', whiteSpace: 'pre-wrap' }}
                      >
                        {msg.text}
                        {msg.sender === 'ai' && (
                          <div className="mt-2 pt-2 border-top">
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => {
                                // Find the previous user message as the question
                                const prevMsg = chatHistory[idx - 1];
                                const question = prevMsg?.sender === 'user' ? prevMsg.text : '';
                                handleShareChatToSlack(msg.text, question);
                              }}
                              className="d-flex align-items-center"
                            >
                              <FaSlack className="me-1" /> Share to Slack
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
                {chatLoading && (
                  <div className="d-flex justify-content-start mb-3">
                    <div className="bg-light p-3 rounded-3 d-flex align-items-center">
                      <Spinner animation="border" size="sm" className="me-2" />
                      <span>Thinking...</span>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Chat input */}
              <InputGroup>
                <Form.Control
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Ask about this transcript..."
                  disabled={chatLoading}
                  as="textarea"
                  rows={1}
                  style={{ resize: 'none' }}
                />
                <Button variant="primary" onClick={handleChat} disabled={chatLoading || !chatInput.trim()}>
                  Send
                </Button>
              </InputGroup>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      {/* Share Modal */}
      <ShareModal 
        show={showShareModal}
        onHide={() => setShowShareModal(false)}
        meetingData={meetingData}
        shareType="all"
      />
    </Container>
  );
};

export default Summary;
